import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { clsx } from 'clsx'

export interface OverflowTextProps {
  /** Полный текст: показывается в подсказке, если превью не помещается */
  text: string
  className?: string
  /** Максимум строк превью; по умолчанию одна строка с многоточием */
  maxLines?: number
  /** Своё превью вместо текста (иконка, бейдж) */
  children?: React.ReactNode
  /**
   * Показывать подсказку всегда, а не только при переполнении: нужно для превью,
   * которое по определению не показывает полный текст (иконка).
   */
  alwaysShowTooltip?: boolean
}

interface TooltipCoords {
  left: number
  maxWidth: number
  top?: number
  bottom?: number
}

// Ширина подсказки повторяет max-w-sm; дальше её ограничивает окно.
const TOOLTIP_MAX_WIDTH = 320
// Задержка закрытия: за это время курсор успевает уйти с ячейки в подсказку.
const CLOSE_DELAY = 120
// Отступ подсказки от ячейки — чтобы она не накрывала саму ячейку и не мешала клику.
const TOOLTIP_OFFSET = 4
// Минимальный запас снизу: если его нет, подсказка показывается над ячейкой.
const SPACE_BELOW_MIN = 120

/**
 * Текст, который сокращается многоточием, но не теряется: полный вариант
 * показывается во всплывающей подсказке. Нативный `title` не подходит —
 * из него нельзя выделить и скопировать текст (нужно логисту для адреса,
 * комментария, телефона курьера).
 *
 * Подсказка рендерится порталом в `document.body` с `position: fixed`:
 * так её не обрезают `overflow` карточки и прокручиваемые контейнеры таблицы.
 */
export const OverflowText: React.FC<OverflowTextProps> = ({
  text,
  className,
  maxLines = 1,
  children,
  alwaysShowTooltip = false,
}) => {
  const previewRef = useRef<HTMLSpanElement>(null)
  const closeTimerRef = useRef<number | null>(null)
  // Клик по ячейке открывает карточку заказа, поэтому после клика подсказку
  // не показываем до ухода курсора — иначе она висела бы поверх карточки.
  const suppressedRef = useRef(false)
  // Курсор внутри подсказки: её нельзя закрывать, иначе пропадёт выделение.
  const insideTooltipRef = useRef(false)

  const [isOverflowing, setIsOverflowing] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [coords, setCoords] = useState<TooltipCoords | null>(null)

  // Подсказка нужна либо по требованию вызывающей стороны (иконка как превью),
  // либо при реальном переполнении — превью с текстом показывается всегда.
  const hasTooltip = alwaysShowTooltip || isOverflowing
  const hasText = text.trim().length > 0

  // Факт сокращения определяем по реальному переполнению элемента, а не «на глаз»:
  // после сужения/расширения окна колонка меняет ширину вместе с текстом.
  const measure = useCallback(() => {
    const node = previewRef.current
    if (!node) return

    const overflows =
      maxLines > 1
        ? node.scrollHeight > node.clientHeight + 1
        : node.scrollWidth > node.clientWidth + 1

    setIsOverflowing(overflows)
    if (!overflows && !alwaysShowTooltip) setIsOpen(false)
  }, [maxLines, alwaysShowTooltip])

  useEffect(() => {
    measure()

    // Inter подключается веб-фонтом: до его применения ширина текста другая,
    // поэтому пересчитываем переполнение после загрузки шрифтов.
    void document.fonts.ready.then(() => measure())

    // ResizeObserver ловит изменения именно контейнера (сужение окна, перенос
    // колонки), window.resize — страховка для браузеров без него.
    const node = previewRef.current
    if (!node || typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure)
      return () => window.removeEventListener('resize', measure)
    }

    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => observer.disconnect()
  }, [measure, text])

  const updatePosition = useCallback(() => {
    const node = previewRef.current
    if (!node) return

    const rect = node.getBoundingClientRect()
    const maxWidth = Math.min(TOOLTIP_MAX_WIDTH, window.innerWidth - 16)
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - maxWidth - 8))
    const spaceBelow = window.innerHeight - rect.bottom
    // У последних строк таблицы снизу места нет — подсказка идёт вверх.
    const placeAbove = spaceBelow < SPACE_BELOW_MIN && rect.top > spaceBelow

    setCoords(
      placeAbove
        ? { left, maxWidth, bottom: window.innerHeight - rect.top + TOOLTIP_OFFSET }
        : { left, maxWidth, top: rect.bottom + TOOLTIP_OFFSET }
    )
  }, [])

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current === null) return
    window.clearTimeout(closeTimerRef.current)
    closeTimerRef.current = null
  }, [])

  const scheduleClose = useCallback(() => {
    cancelClose()
    closeTimerRef.current = window.setTimeout(() => {
      closeTimerRef.current = null
      setIsOpen(false)
    }, CLOSE_DELAY)
  }, [cancelClose])

  const open = useCallback(() => {
    if (!hasTooltip || !hasText) return
    cancelClose()
    updatePosition()
    setIsOpen(true)
  }, [hasTooltip, hasText, cancelClose, updatePosition])

  // Позиционирование при прокрутке таблицы и изменении окна — как в DatePicker.
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false)
    }

    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, updatePosition])

  useEffect(() => () => cancelClose(), [cancelClose])

  const handleMouseEnter = () => {
    if (suppressedRef.current) return
    open()
  }

  const handleMouseLeave = () => {
    // Курсор ушёл с ячейки — следующий вход снова открывает подсказку.
    suppressedRef.current = false
    scheduleClose()
  }

  const handleMouseDown = () => {
    // Клик по строке должен открывать карточку заказа, а не подсказку:
    // гасим её и ждём, пока курсор не покинет ячейку.
    suppressedRef.current = true
    setIsOpen(false)
  }

  const handleFocus = () => {
    // Фокус от клика мышью уже обработан в handleMouseDown; Tab — открывает.
    if (suppressedRef.current) return
    open()
  }

  const handleBlur = () => {
    // Клик внутри подсказки забирает фокус у ячейки, но подсказку закрывать
    // нельзя — пользователь выделяет текст.
    if (insideTooltipRef.current) return
    scheduleClose()
  }

  return (
    <>
      <span
        ref={previewRef}
        // В табуляцию попадают только ячейки, у которых есть подсказка:
        // иначе в таблице на 50 строк появляются сотни лишних точек табуляции.
        tabIndex={hasTooltip ? 0 : -1}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseDown={handleMouseDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        // display можно переопределить через className (например, flex для иконки):
        // утилиты Tailwind идут в CSS после .admin-table td.
        className={clsx(
          'block min-w-0 max-w-full',
          maxLines === 1 ? 'truncate' : 'break-words',
          hasTooltip && 'cursor-help',
          className
        )}
        style={
          maxLines > 1
            ? {
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: maxLines,
                overflow: 'hidden',
              }
            : undefined
        }
      >
        {children ?? text}
      </span>

      {isOpen &&
        hasTooltip &&
        coords &&
        createPortal(
          <div
            className="fixed z-50 max-w-sm select-text whitespace-pre-line break-words rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-lg"
            style={{ ...coords, userSelect: 'text' }}
            onMouseEnter={() => {
              insideTooltipRef.current = true
              cancelClose()
            }}
            onMouseLeave={() => {
              insideTooltipRef.current = false
              scheduleClose()
            }}
          >
            {text}
          </div>,
          document.body
        )}
    </>
  )
}
